import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateProjectFlow } from "components/common/CreateProjectFlow/CreateProjectFlow";
import type { ProjectDataType } from "store/types/project";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import { Permissions } from "utils/permissions/permissionConstants";
import { handleToast } from "utils/toasts";
import type { ProjectCreateSource } from "components/common/CreateProjectFlow/types/project-create-source.types";

export type LaunchCreateProjectOptions = {
  onSuccess?: (project: ProjectDataType) => void;
  initialSource?: ProjectCreateSource;
  /** Opens straight on the import form with this screenplay attached. */
  initialFile?: File;
  entrySource?: "onboarding" | "projects" | "sidebar";
};

export function useCreateProjectLauncher() {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceIdentifiers();
  const { canWorkspace, isLoading: isWorkspaceAuthLoading } = useWorkspaceAuth(workspaceId);
  const [isOpen, setIsOpen] = useState(false);
  const [launchOptions, setLaunchOptions] = useState<LaunchCreateProjectOptions>({});
  const successRef = useRef<(project: ProjectDataType) => void>();

  const canCreateWorkspaceProject = canWorkspace(Permissions.WORKSPACE_PROJECT_CREATE);

  const launchCreateProject = useCallback(
    (opts?: LaunchCreateProjectOptions) => {
      if (isWorkspaceAuthLoading) {
        return;
      }

      if (!canCreateWorkspaceProject) {
        handleToast("You do not have permission to create projects in this workspace.", "error");
        return;
      }

      successRef.current = opts?.onSuccess;
      setLaunchOptions(opts?.initialFile ? { ...opts, initialSource: "import" } : (opts ?? {}));
      setIsOpen(true);
    },
    [canCreateWorkspaceProject, isWorkspaceAuthLoading]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setLaunchOptions({});
  }, []);

  const handleSuccess = useCallback(
    (project: ProjectDataType) => {
      const onSuccess = successRef.current;
      successRef.current = undefined;
      setIsOpen(false);
      onSuccess?.(project);

      // Always land on the new project after create/import (callers may also navigate).
      if (workspaceId && project?.project_id) {
        navigate(`/workspaces/${workspaceId}/projects/${project.project_id}/script/breakdown`);
      }
    },
    [navigate, workspaceId]
  );

  const createProjectFlowElement = useMemo(
    () => (
      <CreateProjectFlow
        isOpen={isOpen}
        onClose={close}
        onSuccess={handleSuccess}
        mode="project"
        initialSource={launchOptions.initialSource}
        initialFile={launchOptions.initialFile}
        entrySource={launchOptions.entrySource}
      />
    ),
    [
      close,
      handleSuccess,
      isOpen,
      launchOptions.entrySource,
      launchOptions.initialFile,
      launchOptions.initialSource,
    ]
  );

  return {
    launchCreateProject,
    createProjectFlowElement,
    isCreateProjectOpen: isOpen,
    canCreateWorkspaceProject,
    isWorkspaceAuthLoading,
  };
}
